import type {
    ClientPortalThread as ExistingThread,
    CustomerOrderTask as OrderTask,
    OrderTaskStatus,
    TaskAssignee,
    CustomerTaskOverview as TaskOverview,
} from '@einwert/valuation-api-client'

import { useGetOrderOverviewByOrderId } from 'ClientPortal/src/api/Queries/orders'
import { useGetTaskQueries, useGetTasks, useOptimisticPatchTask } from 'ClientPortal/src/api/Queries/tasks'
import { useGetThreads } from 'ClientPortal/src/api/Queries/threads'
import { useTypedParams } from 'ClientPortal/src/router/useTypedParams'
import { flow, partial, sum } from 'lodash'
import { UseQueryResult } from '@tanstack/react-query'
import { UUID } from 'ComponentLibrary/src/common/uuid'
import { removeDuplicateTasks } from 'ComponentLibrary'
import { useGetUsers } from 'ClientPortal/src/api/Queries/users'

export function useTaskListPage() {
    const { orderId } = useTypedParams(['orderId'])
    const { users, isUsersPending, isUsersInitialLoading } = useGetUsers({ orderId })
    const { tasks: tasksInOpen, isTasksPending: areOpenTasksPending } = useGetTasks({ orderId, status: 'open' })
    const { tasks: tasksInDone, isTasksPending: areDoneTasksPending } = useGetTasks({ orderId, status: 'done' })

    const taskDetailResults = useGetTaskQueries([...tasksInOpen, ...tasksInDone].map((task) => task.uuid))
    const threadsResults = useGetThreads({ orderId })

    const withMetadataCounts = partial(addTaskMetadataCounts, taskDetailResults, threadsResults.threads)
    const processTasks = flow(withMetadataCounts)

    // There can be a race condition where a task appears on both sections depending on which query finishes first
    // The deduplication step ensures that a transitioning task will only appear once
    // This happens due to the optimistic update being handled incorectly, a task that is optimistically set to done,
    // should not only have its status changed but change its location to the matching query cache.
    const deduplicateAndProcess = flow(
        ({ openTasks, doneTasks }: { openTasks: TaskOverview[]; doneTasks: TaskOverview[] }) => removeDuplicateTasks(openTasks, doneTasks),
        ({ openTasks, doneTasks }) => ({
            openTasks: processTasks(openTasks),
            doneTasks: processTasks(doneTasks),
        })
    )

    const { openTasks: openTasksWithCounts, doneTasks: doneTasksWithCounts } = deduplicateAndProcess({
        openTasks: tasksInOpen,
        doneTasks: tasksInDone,
    })

    const { mutateTask, isPatchingTask: isPendingTaskUpdate } = useOptimisticPatchTask()

    function toggleTaskStatus(task: { uuid: UUID; status: OrderTaskStatus }, invalidationDelayMilliseconds?: number) {
        mutateTask({
            taskId: task.uuid,
            operations: [
                {
                    op: 'replace',
                    path: '/status',
                    value: task.status === 'open' ? 'done' : 'open',
                },
            ],
            invalidationDelayMilliseconds,
        })
    }

    function changeTaskAssignee(taskId: UUID, assignee: TaskAssignee | null) {
        mutateTask({
            taskId,
            operations: [{ op: 'replace', path: '/assignee', value: assignee }],
        })
    }

    return {
        orderId,
        ...useGetOrderOverviewByOrderId(orderId),
        openTasksWithCounts,
        doneTasksWithCounts,
        isTasksPending: areDoneTasksPending || areOpenTasksPending,
        toggleTaskStatus,
        isPendingTaskUpdate,
        users,
        isUsersPending,
        isUsersInitialLoading,
        changeTaskAssignee,
    }
}

function addTaskMetadataCounts(taskDetailResults: UseQueryResult<OrderTask, Error>[], threads: ExistingThread[], taskList: TaskOverview[]) {
    const taskDetailResultsMap = taskDetailResults.reduce(
        (prev, current) => {
            if (current.data?.uuid) {
                return { ...prev, [current.data.uuid]: current }
            }
            return prev
        },
        {} as Record<string, UseQueryResult<OrderTask, Error>>
    )

    return taskList.map((taskListItem) => {
        const taskDetails = taskDetailResultsMap[taskListItem.uuid]
        return Object.assign(taskListItem, {
            attachmentsCount: sum([taskDetails?.data?.documents.length, taskDetails?.data?.images.length]),
            threadsCount: threads.find((thread) => thread.uuid === taskListItem.communication_thread)?.messages.length,
        })
    })
}
